// The integrations' API calls (P6-B01, docs/API.md «Integrations»; DATA_MODEL §7 `integration_configs`): the
// server-side mirror of the store backend's state for Payme, Click, BTS and iCanGroup, the start of merchant
// onboarding and an on-demand re-read, typed by the generated schema.
import { queryOptions } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '@/api/client';

/** The providers the design draws, in its order (TZ §11). */
export const PROVIDERS = ['payme', 'click', 'bts', 'icangroup'] as const satisfies readonly Schemas['Provider'][];
export type Provider = Schemas['Provider'];

/** `integration_configs.server_state`: the store backend's truth; a local «enable» only asks for onboarding. */
export type IntegrationState = Schemas['IntegrationState'];

/**
 * One provider as the organization sees it: the mirror of the backend (`state`, `connected`, the masked
 * `display` «merchant_id ••••4f2a», never a credential), the organization's wish (`enabled`), onboarding
 * (`onboarding_url`, `onboarding_requested_at`), the last read and its failure code, and `can_connect`
 * («Подключить» / «Повторить проверку» may start onboarding).
 */
export type IntegrationItem = Schemas['IntegrationItemOut'];

export type IntegrationsOut = Schemas['IntegrationsOut'];

export const integrationsKey = (orgId: string) => ['integrations', orgId] as const;

/** GET /admin/organizations/{id}/integrations (`integration:read`). Polled while a provider waits for Laravel. */
export const integrationsQuery = (orgId: string) =>
  queryOptions({
    queryKey: integrationsKey(orgId),
    queryFn: (): Promise<IntegrationsOut> => unwrap(api.GET('/admin/organizations/{organization_id}/integrations', { params: { path: { organization_id: orgId } } })),
    refetchInterval: (q) => (q.state.data?.items.some((x) => x.state === 'pending_verification') ? 15_000 : false),
  });

/**
 * «Подключить» / «Повторить проверку» (`integration:connect`): POST …/integrations/{provider}/connect {confirm: true}
 * → 202 {job_id, integration}: the store backend starts merchant onboarding; only its answer ever makes it
 * `connected`. 409 `store_not_provisioned`, `integration_already_connected`, `job_already_running`.
 */
export function connectIntegration(orgId: string, provider: Provider): Promise<Schemas['ConnectStarted']> {
  return unwrap(
    api.POST('/admin/organizations/{organization_id}/integrations/{provider}/connect', {
      params: { path: { organization_id: orgId, provider } },
      body: { confirm: true },
    }),
  );
}

/** «Статус от Laravel»: POST …/integrations/refresh → 202 {job_id} (`integrations.sync` reads the backend now). */
export function refreshIntegrations(orgId: string): Promise<Schemas['RefreshStarted']> {
  return unwrap(api.POST('/admin/organizations/{organization_id}/integrations/refresh', { params: { path: { organization_id: orgId } } }));
}

/** The organization's row per provider (the API lists all four; a missing one reads as unknown). */
export function byProvider(rows: IntegrationItem[]): Record<Provider, IntegrationItem> {
  const out = {} as Record<Provider, IntegrationItem>;
  for (const p of PROVIDERS)
    out[p] = rows.find((r) => r.provider === p) ?? {
      provider: p,
      state: null,
      connected: false,
      display: null,
      enabled: null,
      onboarding_url: null,
      onboarding_requested_at: null,
      last_synced_at: null,
      last_error: null,
      can_connect: false,
    };
  return out;
}

/**
 * The masked label as shown: the backend's («merchant_id ••••4f2a»), or — should it ever send an unmasked value —
 * only its last four characters. A full identifier never reaches the screen.
 */
export function maskedId(display: string | null): string | null {
  if (!display) return null;
  const v = display.trim();
  if (!v) return null;
  if (v.includes('•')) return v;
  return `••••${v.slice(-4)}`;
}
